import { type ComponentProps } from 'react';

import {
	Dialog as ArkDialog,
	type DialogRootProps as ArkDialogRootProps,
	type HTMLArkProps,
	Portal,
	ark
} from '@ark-ui/react';

import type { DialogSize } from '../../lib/types';
import { cn } from '../../lib/utils';

type DialogRootProps = ArkDialogRootProps;

function DialogRoot({ lazyMount = true, unmountOnExit = true, ...props }: DialogRootProps) {
	return <ArkDialog.Root lazyMount={lazyMount} unmountOnExit={unmountOnExit} {...props} />;
}

function DialogTrigger({ className, ...props }: ComponentProps<typeof ArkDialog.Trigger>) {
	return <ArkDialog.Trigger className={className} {...props} />;
}

function DialogBackdrop({ className, ...props }: ComponentProps<typeof ArkDialog.Backdrop>) {
	return (
		<Portal>
			<ArkDialog.Backdrop className={cn('pt-dialog-backdrop', className)} {...props} />
		</Portal>
	);
}

type DialogContentProps = ComponentProps<typeof ArkDialog.Content> & {
	size?: DialogSize;
	align?: 'start' | 'center';
};

function DialogContent({
	className,
	size = '3',
	align = 'center',
	children,
	...props
}: DialogContentProps) {
	return (
		<Portal>
			<ArkDialog.Backdrop className="pt-dialog-backdrop" />
			<ArkDialog.Positioner className="pt-dialog-positioner" data-align={align}>
				<ArkDialog.Content
					className={cn('pt-dialog-content', className)}
					data-size={size}
					{...props}
				>
					{children}
				</ArkDialog.Content>
			</ArkDialog.Positioner>
		</Portal>
	);
}

function DialogTitle({ className, ...props }: ComponentProps<typeof ArkDialog.Title>) {
	return <ArkDialog.Title className={cn('pt-dialog-title', className)} {...props} />;
}

function DialogDescription({ className, ...props }: ComponentProps<typeof ArkDialog.Description>) {
	return <ArkDialog.Description className={cn('pt-dialog-description', className)} {...props} />;
}

function DialogClose({ className, ...props }: ComponentProps<typeof ArkDialog.CloseTrigger>) {
	return <ArkDialog.CloseTrigger className={cn('pt-dialog-close', className)} {...props} />;
}

function DialogBody({ className, ...props }: HTMLArkProps<'div'>) {
	return <ark.div className={cn('pt-dialog-body', className)} {...props} />;
}

function DialogFooter({ className, ...props }: HTMLArkProps<'div'>) {
	return <ark.div className={cn('pt-dialog-footer', className)} {...props} />;
}

const Dialog = Object.assign(DialogRoot, {
	Root: DialogRoot,
	Trigger: DialogTrigger,
	Backdrop: DialogBackdrop,
	Content: DialogContent,
	Title: DialogTitle,
	Description: DialogDescription,
	Close: DialogClose,
	Body: DialogBody,
	Footer: DialogFooter
});

export { Dialog, type DialogRootProps, type DialogContentProps };
